/**
 * Sidewall Color Constants
 * Domain colors for the EAF Sidewall module (panel identification, components)
 *
 * NOTE: UI colors (tooltips, overlays, gradients) are now in the shared EAF theme.
 * Access them via theme.palette.eqp.viewer.* in MUI components.
 */

import { SPECIAL_POSITION_IDS } from "../models";

/**
 * SVG thumbnail UI colors
 */
export const SidewallSvgColors = {
  BACKGROUND: "#2d2d2d", // Dark gray background
  STROKE: "#1a1a1a", // Dark stroke for borders
  CENTER_AREA: "#404040", // Medium gray for center
  PANEL_TEXT: "#ffffff", // White text for panel numbers
  BURNER_FILL: "#FFA500", // Orange for burner indicators
  BURNER_STROKE: "#ffffff", // White stroke for burner indicators
} as const;

// =============================================================================
// DOMAIN COLORS - Physical positions (should NOT change with theme)
// =============================================================================

/**
 * Panel colors (each panel has unique color)
 *
 * Colors are arranged so that adjacent panels (in the circular layout)
 * have maximum visual contrast. The pattern alternates between warm and
 * cool colors, ensuring no similar hues are neighbors.
 *
 * Layout (circular): 1-2-3-4-5-6-7-8-9-10-11-12-1
 */
export const PANEL_COLORS: Record<number, string> = {
  1: "#E91E8C", // Magenta (Panel 1)
  2: "#2E7D32", // Dark Green (Panel 2) - contrasts with Magenta
  3: "#F44336", // Red (Panel 3 - #1 Burner)
  4: "#2196F3", // Blue (Panel 4) - contrasts with Red
  5: "#9C27B0", // Purple (Panel 5 - #2 Burner)
  6: "#FF9800", // Orange (Panel 6) - contrasts with Purple
  7: "#00BCD4", // Cyan (Panel 7)
  8: "#E65100", // Deep Orange (Panel 8) - contrasts with Cyan
  9: "#8BC34A", // Light Green (Panel 9 - #3 Burner)
  10: "#3F51B5", // Indigo (Panel 10) - contrasts with Light Green
  11: "#FFEB3B", // Yellow (Panel 11)
  12: "#7B1FA2", // Deep Purple (Panel 12) - contrasts with Yellow
};

/**
 * Colors for special components
 *
 * Note: Shell colors use metallic tones to represent the furnace structure
 * and differentiate from EMPTY (gray) status
 */
export const COMPONENT_COLORS = {
  ARCHWAY: "#00BCD4", // Cyan
  SLAG_DOOR: "#795548", // Brown
  LEFT_SUMP: "#FFEB3B", // Yellow
  RIGHT_SUMP: "#2196F3", // Blue
  CENTER_SUMP: "#00BCD4", // Cyan
  LOWER_SHELL: "#37474F", // Blue Gray 800 - Dark steel color
  UPPER_SHELL: "#546E7A", // Blue Gray 600 - Lighter steel color
  BURNER_BLOCK: "#8D6E63", // Brown
  BURNER_FRONT_FACE: "#A1887F", // Light Brown
  BURNER_WATER_JACKET: "#4FC3F7", // Light Blue
  BURNER_NOZZLE: "#607D8B", // Blue Gray
  EMPTY: "#9E9E9E", // Gray for empty positions
} as const;

/**
 * Status-based colors (same as Roof for consistency)
 */
export const STATUS_COLORS = {
  OK: undefined, // Use default panel color
  WARNING: "#FFC107", // Yellow/Amber
  ALERT: "#F44336", // Red
  BLANK: "#9E9E9E", // Gray
} as const;

// =============================================================================
// COLOR LOOKUP MAPS - Direct mappings for O(1) lookups
// =============================================================================

/**
 * Maps special position IDs to their colors
 */
const POSITION_COLOR_MAP: Record<number, string> = {
  [SPECIAL_POSITION_IDS.LEFT_SUMP]: COMPONENT_COLORS.LEFT_SUMP,
  [SPECIAL_POSITION_IDS.RIGHT_SUMP]: COMPONENT_COLORS.RIGHT_SUMP,
  [SPECIAL_POSITION_IDS.CENTER_SUMP]: COMPONENT_COLORS.CENTER_SUMP,
  [SPECIAL_POSITION_IDS.ARCHWAY]: COMPONENT_COLORS.ARCHWAY,
  [SPECIAL_POSITION_IDS.SLAG_DOOR]: COMPONENT_COLORS.SLAG_DOOR,
  [SPECIAL_POSITION_IDS.LOWER_SHELL]: COMPONENT_COLORS.LOWER_SHELL,
  [SPECIAL_POSITION_IDS.UPPER_SHELL]: COMPONENT_COLORS.UPPER_SHELL,
};

// =============================================================================
// COLOR GETTER FUNCTIONS
// =============================================================================

/**
 * Get the color for a panel position (1-12)
 */
export const getPanelColor = (positionId: number): string =>
  PANEL_COLORS[positionId] ?? COMPONENT_COLORS.EMPTY;

/**
 * Get the color for a special component by position.
 * Falls back to panel color if not a special component.
 */
export const getComponentColor = (positionId: number): string =>
  POSITION_COLOR_MAP[positionId] ?? getPanelColor(positionId);
